import { en } from './locales/en/en.index'
import type { I18nKey } from './types'

/**
 * Checks if a string is a valid translation key by verifying it exists in the translations.
 * This is a runtime check that ensures type safety.
 *
 * @param key - The string to check
 * @returns True if the key exists in translations and points to a string value
 *
 * @example
 * ```typescript
 * isValidTranslationKey("common.save") // true
 * isValidTranslationKey("Team Alpha") // false
 * isValidTranslationKey("invalid.key") // false
 * ```
 */
export function isValidTranslationKey(key: string): key is I18nKey {
  try {
    // Split the key by dots to navigate the nested object
    const keys = key.split('.')
    let current: unknown = en

    for (const k of keys) {
      if (current && typeof current === 'object' && current !== null && k in current) {
        current = (current as Record<string, unknown>)[k]
      } else {
        return false
      }
    }

    // If we can navigate to the final value, it's a valid key
    return typeof current === 'string'
  } catch {
    return false
  }
}
